<script setup lang="ts">
import { formatUsd, JOB_PACK_APPLICATIONS, JOB_PACK_DAYS, separateToolsMonthly } from '~/constants/pricing'

/**
 * The pack as a small offer card for content pages (feature pages, blog
 * posts): what it gives in four items, the price with the per-application
 * figure, the same "bought separately" total as the full stack on /pricing
 * (whose footnote lists the plans behind it), and a free way in first: the
 * builder on the homepage, or the editor for a signed-in visitor. The
 * market's prices are dollars as they are; ours are the visitor's (usePricing).
 */
const { t } = useI18n()
const { pricing } = usePricing()
const { isAuthenticated } = useAuthUser()

const features = [
  { key: 'pdf', icon: 'bi-file-earmark-check' },
  { key: 'letters', icon: 'bi-envelope-paper' },
  { key: 'ats', icon: 'bi-bullseye' },
  { key: 'website', icon: 'bi-globe2' },
] as const

const price = computed(() => pricing.value.format(pricing.value.prices.jobPack))
const perApplication = computed(() => pricing.value.format(pricing.value.prices.jobPack / JOB_PACK_APPLICATIONS))
const tryTarget = computed(() => (isAuthenticated.value ? '/cv' : '/'))
</script>

<template>
  <div class="overflow-hidden rounded-[0.625rem] border border-line bg-surface dark:border-gray-800 dark:bg-gray-900">
    <div class="grid gap-6 p-5 sm:p-7 md:grid-cols-[1fr_17.5rem] md:items-center md:gap-8">
      <div>
        <span class="inline-flex items-center gap-1.5 rounded-full bg-accent-soft/50 px-3 py-1 text-xs font-semibold text-ink dark:bg-accent/20 dark:text-accent-soft">
          <i class="bi bi-briefcase-fill" aria-hidden="true" />
          {{ t('premium.jobPack.note.badge') }}
        </span>
        <div class="display-heading display-heading-5 mt-3">{{ t('premium.jobPack.note.title') }}</div>
        <div class="mt-1.5 text-base leading-relaxed">{{ t('premium.jobPack.note.subtitle') }}</div>

        <ul class="mt-5 grid list-none gap-3 p-0 sm:grid-cols-2">
          <li v-for="item in features" :key="item.key" class="flex items-center gap-3">
            <span class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-white text-lg text-accent shadow-sm dark:bg-gray-800 dark:text-accent-soft">
              <i class="bi" :class="item.icon" aria-hidden="true" />
            </span>
            <span class="text-[0.9375rem] font-semibold leading-snug text-ink dark:text-white">
              {{ t(`premium.jobPack.note.features.${item.key}`) }}
            </span>
          </li>
        </ul>
      </div>

      <div class="rounded-[0.625rem] bg-white p-5 text-center shadow-sm dark:bg-gray-950">
        <div class="flex items-baseline justify-center gap-2">
          <span class="font-title text-4xl font-bold text-ink dark:text-white">{{ price }}</span>
          <span class="text-sm">{{ t('premium.jobPack.note.price', { days: JOB_PACK_DAYS }) }}</span>
        </div>
        <div class="mt-1 text-sm font-semibold text-accent-strong dark:text-accent-soft">
          {{ t('premium.jobPack.note.perApplication', { perApplication, count: JOB_PACK_APPLICATIONS }) }}
        </div>
        <div class="mt-0.5 text-xs text-muted dark:text-gray-400">
          {{ t('premium.jobPack.note.separately', { separately: formatUsd(separateToolsMonthly()) }) }}
        </div>

        <div class="mt-5 flex flex-col items-center gap-3">
          <SofaxBtn :to="tryTarget" :label="t('premium.jobPack.note.tryCta')" block />
          <SofaxArrowLink to="/pricing#plans" :label="t('premium.jobPack.note.cta')" class="text-[0.9375rem]" />
        </div>
      </div>
    </div>

    <div v-if="!isAuthenticated" class="flex items-center gap-2 border-t border-line px-5 py-3 text-sm sm:px-7 dark:border-gray-800">
      <i class="bi bi-gift shrink-0 text-accent dark:text-accent-soft" aria-hidden="true" />
      <span>
        {{ t('premium.jobPack.note.tryHint') }}
        <LocaleLink to="/registration" class="font-semibold text-ink underline underline-offset-2 hover:text-accent dark:text-white">{{ t('premium.jobPack.note.registerLink') }}</LocaleLink>.
      </span>
    </div>
  </div>
</template>
